import React, { useEffect, useState } from 'react';
import { bookingApi } from '../../utils/bookingApi';
import { AREAS, AREA_LABELS, areaShown } from '../../utils/access';
import { useFeatures } from '../../hooks/useFeature';
import { roleLabel } from '../../pages/booking/admin/memberShared';
import { useConfirm, useToast } from '../ui';
import './StaffRoles.css';

// One line on what a role can do: the areas it manages, then the ones it
// can only view. Areas for features the club has switched off are left out.
export function roleSummary(role, featureOn) {
  if (role.builtIn === 'CLUB_ADMIN') return 'Everything';
  const names = (level) => AREAS.filter(a => role.access?.[a] === level && areaShown(a, featureOn)).map(a => AREA_LABELS[a]);
  const manage = names('MANAGE');
  const view = names('VIEW');
  const parts = [];
  if (manage.length) parts.push(`Manage: ${manage.join(', ')}`);
  if (view.length) parts.push(`View: ${view.join(', ')}`);
  return parts.length ? parts.join('. ') : 'No access to anything yet';
}

const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));

// The roles a member of staff holds, as tick boxes (Club admins only).
// Ticking none makes them an ordinary member again. `roles` is the club's
// roles if the page already has them; otherwise they're loaded here.
export default function StaffRoles({ member, roles: givenRoles, onSaved }) {
  const [roles, setRoles] = useState(givenRoles || null);
  const [loadError, setLoadError] = useState(null);
  const [held, setHeld] = useState(member.roleIds || []);
  const [heldRole, setHeldRole] = useState(member.role);
  const [ticked, setTicked] = useState(member.roleIds || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const featureOn = useFeatures();
  const confirm = useConfirm();
  const toast = useToast();

  useEffect(() => { if (givenRoles) setRoles(givenRoles); }, [givenRoles]);
  useEffect(() => {
    if (givenRoles) return;
    bookingApi.getRoles()
      .then(r => setRoles(r.data))
      .catch(err => setLoadError(err.response?.data?.error || "Couldn't load the roles."));
  }, [givenRoles]);

  const toggle = (id) => {
    setTicked(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));
  };

  const adminId = roles?.find(r => r.builtIn === 'CLUB_ADMIN')?.id;
  const givesAdmin = adminId && ticked.includes(adminId) && !held.includes(adminId);

  const save = async () => {
    if (givesAdmin) {
      const ok = await confirm({
        title: 'Make them a Club admin?',
        message: 'Club admin gives full access to everything in the club.',
        confirmLabel: 'Make admin',
        tone: 'primary',
      });
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const ordered = roles.filter(r => ticked.includes(r.id)).map(r => r.id);
      const res = await bookingApi.setUserRoles(member.id, ordered);
      const roleIds = res.data?.roleIds || ordered;
      setHeld(roleIds);
      setHeldRole(res.data?.role || (roleIds.length ? member.role : 'ADULT'));
      setTicked(roleIds);
      toast('Roles saved');
      onSaved?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't save the roles.");
    } finally {
      setSaving(false);
    }
  };

  const current = roleLabel({ role: heldRole, roleIds: held }, roles);

  return (
    <div className="staff-roles">
      <p className="staff-roles__current"><span className="bk-muted">Now:</span> <strong>{current}</strong></p>
      {loadError && <p className="bk-error">{loadError}</p>}
      {!roles && !loadError && <p className="bk-muted" role="status">Loading roles…</p>}
      {roles && (
        <fieldset className="staff-roles__list">
          <legend className="staff-roles__legend">Roles</legend>
          {roles.map(r => (
            <label key={r.id} className="staff-roles__item">
              <input type="checkbox" className="bk-checkbox" checked={ticked.includes(r.id)} disabled={saving}
                onChange={() => toggle(r.id)} />
              <span>
                <span className="staff-roles__name">{r.name}</span>
                <small className="staff-roles__summary">{roleSummary(r, featureOn)}</small>
              </span>
            </label>
          ))}
          {ticked.length === 0 && (
            <p className="bk-muted staff-roles__note">With no roles ticked they're an ordinary member, with no staff access.</p>
          )}
        </fieldset>
      )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {roles && (
        <div className="bk-actions">
          <button type="button" className="bk-btn bk-btn--primary" disabled={saving || sameSet(ticked, held)} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
